import { useState } from 'react';
import { showSuccess, showError } from '@/utils/toastHelpers';
import { useCreateSponsor, useUpdateSponsor, useDeleteSponsor, useUploadSponsorImage } from '@/hooks/useSponsors';

export const useSponsorsActions = (selectedRaceId: string, sponsorsCount: number) => {
  const createSponsor = useCreateSponsor(selectedRaceId);
  const updateSponsor = useUpdateSponsor(selectedRaceId);
  const deleteSponsorMut = useDeleteSponsor(selectedRaceId);
  const uploadImage = useUploadSponsorImage();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [useExternalImage, setUseExternalImage] = useState(false);
  const [formData, setFormData] = useState({
    name: '', imageUrl: '', isExternalImage: false,
    websiteUrl: '', type: 'Sponsor', sortOrder: 0,
  });

  const handleAdd = () => {
    setEditingSponsor(null);
    setUseExternalImage(false);
    setFormData({
      name: '', imageUrl: '', isExternalImage: false,
      websiteUrl: '', type: 'Sponsor', sortOrder: sponsorsCount,
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (sponsor: any) => {
    setEditingSponsor(sponsor);
    setUseExternalImage(sponsor.isExternalImage);
    setFormData({
      name: sponsor.name, imageUrl: sponsor.imageUrl || '',
      isExternalImage: sponsor.isExternalImage,
      websiteUrl: sponsor.websiteUrl || '', type: sponsor.type,
      sortOrder: sponsor.sortOrder,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: any) => setDeleteTarget(id);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSponsorMut.mutateAsync(deleteTarget);
      showSuccess('Успешно избришано', 'Спонзорот е успешно избришан.');
    } catch (error) {
      showError(error, 'спонзорот');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      showError({ message: 'Дозволени формати: JPG, PNG, GIF, WebP, SVG' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError({ message: 'Максимална големина на слика е 5MB' });
      return;
    }

    try {
      setUploading(true);
      const result: any = await uploadImage.mutateAsync(file);
      setFormData((prev) => ({ ...prev, imageUrl: result.imageUrl, isExternalImage: false }));
      showSuccess('Успешно', 'Сликата е успешно прикачена.');
    } catch (error) {
      showError(error, 'сликата');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRaceId) {
      showError({ message: 'Изберете настан прво' });
      return;
    }

    try {
      const sponsorData = {
        name: formData.name,
        imageUrl: formData.imageUrl || null,
        isExternalImage: useExternalImage,
        websiteUrl: formData.websiteUrl || null,
        type: formData.type,
        sortOrder: parseInt(String(formData.sortOrder)) || 0,
      };

      if (editingSponsor) {
        await updateSponsor.mutateAsync({ id: editingSponsor.id, data: sponsorData });
        showSuccess('Успешно ажурирано', 'Спонзорот е успешно ажуриран.');
      } else {
        await createSponsor.mutateAsync(sponsorData);
        showSuccess('Успешно креирано', 'Нов спонзор е успешно креиран.');
      }

      setIsDialogOpen(false);
      setEditingSponsor(null);
    } catch (error) {
      showError(error, 'спонзорот');
    }
  };

  return {
    isDialogOpen, setIsDialogOpen,
    editingSponsor, useExternalImage, setUseExternalImage,
    deleteTarget, setDeleteTarget,
    formData, setFormData, uploading,
    handleAdd, handleEdit, handleDelete, confirmDelete,
    handleImageUpload, handleSubmit,
  };
};
